<template>
  <HintBox v-if="isVisibleHintBox" :ref-target="micSettingsButton" />

  <ButtonTemplateMicInput
    :on-click="toggleMicPopup"
    :set-ref-button="(el) => (micSettingsButton = el)"
    :is-open="isVisiblePopupMic"
    :has-notification="hasNotification"
  >
    <template #icon-handler>
      <IconController :data-selected="isVisiblePopupMic" />
    </template>
  </ButtonTemplateMicInput>

  <Teleport to="body">
    <DropdownBottom
      v-show="isVisiblePopupMic"
      :target-el="micSettingsButton"
      :is-visible="isVisiblePopupMic"
      @close="closeMicPopup"
    >
      <MicSetting
        :is-visible="isVisiblePopupMic"
        :mode="mode"
        :using-tss="usingTss"
        :is-using-select-language="isUsingSelectLanguage"
      />
    </DropdownBottom>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue';

import { storeToRefs } from 'pinia';

import ButtonTemplateMicInput from '@/components/domains/chat/common/ButtonTemplateMicInput.vue';
import MicSetting from '@/components/domains/chat/common/MicSetting.vue';
import IconController from '@/components/icons/IconController.vue';
import DropdownBottom from '@/components/shared/base/DropdownBottom.vue';
import HintBox from '@/components/shared/composite/HintBox.vue';
import { useToastStore } from '@/stores/toast.js';
import { MicrophoneMode } from '@/types/components';
import { ij_currentPopupMicInput, ij_setCurrentPopupMicInput } from '@/utils/injectionKeys.js';

const toastStore = useToastStore();
const { showHintBox } = storeToRefs(toastStore);

const currentPopupMicInput = inject(ij_currentPopupMicInput, ref(''));
const setCurrentPopupMicInput = inject(ij_setCurrentPopupMicInput) as (popup: string) => void;

withDefaults(
  defineProps<{
    usingTss?: boolean;
    mode?: MicrophoneMode;
    isUsingSelectLanguage?: boolean;
  }>(),
  {
    usingTss: false,
    mode: '',
    isUsingSelectLanguage: false,
  }
);

const micSettingsButton = ref<HTMLElement>();
const hasNotification = ref<boolean>(false);

const isVisiblePopupMic = computed<boolean>(() => {
  return currentPopupMicInput.value === 'mic';
});

const isVisibleHintBox = computed<boolean>(() => {
  return showHintBox.value === 'mic' && !isVisiblePopupMic.value;
});

const toggleMicPopup = () => {
  setCurrentPopupMicInput('mic');
  if (currentPopupMicInput.value === 'mic') {
    hasNotification.value = false;
  }
};

const closeMicPopup = () => {
  if (currentPopupMicInput.value === 'mic') {
    setCurrentPopupMicInput('');
  }
};

onMounted(() => {
  const stopNotification = watch(
    () => isVisibleHintBox.value,
    () => {
      hasNotification.value = true;
      stopNotification();
    }
  );
});
</script>

<style scoped lang="scss">
::v-deep(.button-mic) {
  min-width: 2rem;
  padding: 0.75rem 0 !important;
}
</style>
